import React from "react";
import { TRADE_FILTER_TEXT_CLASS } from "@/components/trade-filter-selects";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select as ShadcnSelect,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { SimulateLegs } from "../../../types/managementExplorer";
import {
  simulateLegOptions,
  type ExitPlacementLegAvailability,
} from "../../../utils/exitPlacementSimLegs";

const SIMULATOR_TOOLBAR_SIM_LEGS_FIELD_CLASS: Record<"picks" | "builder", string> = {
  picks: "w-[220px] min-w-[220px] shrink-0",
  builder: "w-[300px] min-w-[300px] shrink-0",
};

const SIMULATOR_OPTION_FIELD_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]";

export type ExitPlacementSimLegSelectorProps = {
  id: string;
  variant: "picks" | "builder";
  value: SimulateLegs;
  onChange: (value: SimulateLegs) => void;
  availability: ExitPlacementLegAvailability;
  disabled?: boolean;
  inlineError?: string | null;
  className?: string;
};

/** Exit Placement leg selector — same toolbar slot as Regime Remove / Keep action. */
export function ExitPlacementSimLegSelector({
  id,
  variant,
  value,
  onChange,
  availability,
  disabled,
  inlineError,
  className,
}: ExitPlacementSimLegSelectorProps): React.ReactElement {
  const options = simulateLegOptions(availability).filter((o) => !o.hidden);

  return (
    <Field
      data-simulator-toolbar-field=""
      data-field="simulate-legs"
      className={className ?? SIMULATOR_TOOLBAR_SIM_LEGS_FIELD_CLASS[variant]}
    >
      <FieldLabel htmlFor={id} className={SIMULATOR_OPTION_FIELD_LABEL_CLASS}>
        Simulate legs
      </FieldLabel>
      <div data-trade-filter-bar="" data-trade-filter-bar-layout="full" className="w-full min-w-0">
        <ShadcnSelect
          value={value}
          onValueChange={(next: string) => onChange(next as SimulateLegs)}
          disabled={disabled || options.length === 0}
        >
          <SelectTrigger id={id} size="sm" aria-required className={`${TRADE_FILTER_TEXT_CLASS} w-full`}>
            <SelectValue placeholder="Select legs" />
          </SelectTrigger>
          <SelectContent data-trade-log-filter-select-content="">
            <SelectGroup>
              {options.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  className={TRADE_FILTER_TEXT_CLASS}
                  title={opt.tooltip}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </ShadcnSelect>
      </div>
      {inlineError ? (
        <p className="mt-1 text-xs text-destructive" role="alert">
          {inlineError}
        </p>
      ) : null}
    </Field>
  );
}
